import {
  InquiryDuplicateReconciliation,
  SkeletonLoader,
} from '@equip.health/ui';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  formatEntitiesForMerge,
  formatMergeRequest,
} from '~/lib/util/inquiry.util';
import { MergeDuplicatesConfirmation } from './confirmation';
import {
  EitherComparisonEntity,
  MergeConfirmationActionType,
  NormalizeDuplicateResponseData,
} from '~/lib/constants/inquiry.constants';
import { areRecordsIdentical, findNextSourceIndex, MergedRecord } from './util';

interface InquiryMergeDuplicatesProps {
  duplicateRecords: NormalizeDuplicateResponseData[];
  id: string;
  isLoadingDuplicates: boolean;
  isMergingDuplicates: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSubmitMerge: (
    formattedRequest: MergeDuplicateContactsAndCoveragesRequestParams,
  ) => void;
}

export const InquiryMergeDuplicates = ({
  duplicateRecords,
  id,
  isLoadingDuplicates,
  isMergingDuplicates,
  isOpen,
  onClose,
  onSubmitMerge,
}: InquiryMergeDuplicatesProps) => {
  const TOPLEVEL_ID = `${id}__merge-duplicates`;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mergedRecords, setMergedRecords] = useState<MergedRecord[]>([]);
  const [pendingAction, setPendingAction] =
    useState<MergeConfirmationActionType | null>(null);
  const [componentRefreshInstanceId, setComponentRefreshInstanceId] =
    useState(0);

  const currentPair = useMemo(
    () => duplicateRecords?.[currentIndex],
    [duplicateRecords, currentIndex],
  );
  const recordA = currentPair?.baseEntity || null;
  const recordB = currentPair?.matchedEntities?.[0] || null;

  const submitDecisions = useCallback(
    (mergedRecords: MergedRecord[]) => {
      if (mergedRecords.length > 0) {
        onSubmitMerge(formatMergeRequest(mergedRecords, id));
      } else {
        onClose();
      }
    },
    [id, onClose, onSubmitMerge],
  );

  const autoMergeRequest = useMemo<
    MergeDuplicateContactsData | MergeDuplicateCoveragesData
  >(() => {
    if (recordA && recordB && areRecordsIdentical(recordA, recordB)) {
      return formatEntitiesForMerge(recordA, recordA, recordB);
    }
    return null;
  }, [recordA, recordB]);

  const isSubmitting = useRef(false);

  useEffect(() => {
    if (isSubmitting.current || isMergingDuplicates || !autoMergeRequest) {
      return;
    }

    const newMergedRecords = [...mergedRecords, autoMergeRequest];
    const nextSourceIndex = findNextSourceIndex(
      duplicateRecords,
      newMergedRecords,
      currentIndex,
    );
    setCurrentIndex(nextSourceIndex);
    if (nextSourceIndex >= duplicateRecords?.length) {
      isSubmitting.current = true;
      submitDecisions(newMergedRecords);
    } else {
      setMergedRecords(newMergedRecords);
      setComponentRefreshInstanceId((prev) => prev + 1);
    }
  }, [
    autoMergeRequest,
    currentIndex,
    duplicateRecords,
    isMergingDuplicates,
    mergedRecords,
    submitDecisions,
  ]);

  const confirmationSubmit = useCallback(() => {
    if (pendingAction === MergeConfirmationActionType.CLOSE) {
      setPendingAction(null);
      onClose();
      return;
    }
    setPendingAction(null);
    const nextSourceIndex = findNextSourceIndex(
      duplicateRecords,
      mergedRecords,
      currentIndex,
    );
    setCurrentIndex(nextSourceIndex);
    if (nextSourceIndex >= duplicateRecords?.length) {
      submitDecisions(mergedRecords);
    } else {
      setComponentRefreshInstanceId((prev) => prev + 1);
    }
  }, [
    currentIndex,
    duplicateRecords,
    mergedRecords,
    onClose,
    pendingAction,
    submitDecisions,
  ]);

  const handleKeepBoth = useCallback(
    () => setPendingAction(MergeConfirmationActionType.KEEP),
    [],
  );

  const handleMerge = useCallback(
    (mergedRecord: EitherComparisonEntity) => {
      setMergedRecords((prev) => [
        ...prev,
        formatEntitiesForMerge(mergedRecord, recordA, recordB),
      ]);
      setPendingAction(MergeConfirmationActionType.MERGE);
    },
    [recordA, recordB],
  );

  const handleBack = () => {
    if (pendingAction && pendingAction === MergeConfirmationActionType.MERGE) {
      setMergedRecords((prev) => prev.slice(0, -1));
    }
    setPendingAction(null);
  };

  const handleClose = useCallback(() => {
    if (pendingAction && pendingAction !== MergeConfirmationActionType.CLOSE) {
      return setPendingAction(MergeConfirmationActionType.CLOSE);
    }
    onClose();
  }, [pendingAction, onClose]);

  return (
    <SkeletonLoader id={TOPLEVEL_ID} isLoaded={!isLoadingDuplicates}>
      {recordA && recordB && (
        <InquiryDuplicateReconciliation
          entityType={recordA.entityType}
          isOpen={!autoMergeRequest && isOpen}
          key={componentRefreshInstanceId}
          onClose={handleClose}
          onKeepBoth={handleKeepBoth}
          onMerge={handleMerge}
          recordA={recordA}
          recordB={recordB}
        />
      )}
      {pendingAction && (
        <MergeDuplicatesConfirmation
          action={pendingAction}
          entityType={recordA.entityType}
          id={id}
          isLoading={isMergingDuplicates}
          onBack={handleBack}
          onClose={handleClose}
          onSubmit={confirmationSubmit}
        />
      )}
    </SkeletonLoader>
  );
};
